'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useIntersectionObserver, useMap } from 'usehooks-ts';
import CreatorCard from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/CreatorCard';
import { useSearchPageContext } from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/SearchPageContext';
import SectionHeader from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/SectionHeader';
import { LoadingDots } from '@/components/LoadingDots';
import { SEARCH_PAGE_LIMIT, SEARCH_PARAM_KEYS, SEARCH_TOP_CREATORS_LIMIT } from '@/constants/search';
import { useConfigsContext } from '@/context/ConfigsContext';
import { fetchFollowStatusForAccounts } from '@/lib/search';
import { getSearchAccounts, PaginatedResponse, SearchAccount } from '@/services/cms';
import { FollowStatusData } from '@/services/community';
type CreatorsListProps = {
  enablePagination: boolean;
};
const convertAccountToCreatorCardFormat = (account: SearchAccount, followStatusMap: Omit<Map<string, FollowStatusData>, 'set' | 'clear' | 'delete'>) => {
  const status = followStatusMap.get(account.community_user_id);
  return {
    name: account.display_name || '',
    username: `@${account.community_user_id}`,
    profileId: account.community_user_id,
    avatar: account.head_icon_url || '',
    followers: status?.followersCount?.toString() || '0',
    following: status?.followingCount?.toString() || '0',
    description: '',
    viverse_pro: account.viverse_pro,
    verified_creator: account.verified_creator
  };
};
const getKey = (pageIndex: number, previousPageData: PaginatedResponse<SearchAccount> | null | undefined, keyword: string, tab: string | null) => {
  if (!keyword) return null;
  const params = new URLSearchParams({
    [SEARCH_PARAM_KEYS.KEYWORD]: keyword
  });
  params.set(SEARCH_PARAM_KEYS.TAB, tab || 'top');
  params.set('start', '0');
  if (pageIndex === 0) return `searchCreators?${params.toString()}`;
  const next = previousPageData?.pages?.next;
  if (!next) return null;
  const start = new URLSearchParams(next.split('?')[1]).get('start');
  if (!start) return null;
  params.set('start', start);
  return `searchCreators?${params.toString()}`;
};
const createFetcher = (htcToken: string | undefined, updateFollowStatusMap: (statuses: FollowStatusData[]) => void) => {
  return async (key: string) => {
    const query = key.split('?')[1];
    const params = new URLSearchParams(query);
    const start = Number(params.get('start'));
    const keyword = params.get(SEARCH_PARAM_KEYS.KEYWORD) || '';
    const tab = params.get(SEARCH_PARAM_KEYS.TAB);
    const isTop = !tab || tab === 'top';
    const response = await getSearchAccounts({
      keyword,
      limit: isTop ? SEARCH_TOP_CREATORS_LIMIT : SEARCH_PAGE_LIMIT,
      start
    }, false);
    const data = response?.results;
    if (data?.results && data.results.length > 0) {
      const followStatusData = await fetchFollowStatusForAccounts(data.results, htcToken);
      if (followStatusData && followStatusData.length > 0) {
        updateFollowStatusMap(followStatusData);
      }
    }
    return data;
  };
};
const createFallbackData = (initialAccounts: SearchAccount[], initialTotal?: number) => {
  if (initialAccounts.length === 0) {
    return undefined;
  }
  return [{
    results: initialAccounts,
    pages: {
      total: initialTotal || 0,
      start: 0,
      limit: SEARCH_PAGE_LIMIT
    }
  }];
};
export default function CreatorsList(t0) {
  const $ = _c(60);
  const {
    enablePagination
  } = t0;
  const {
    accounts: t1,
    accountsTotal: initialTotal,
    followStatus: t2
  } = useSearchPageContext();
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const initialAccounts = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const followStatus = t4;
  const t = useTranslations();
  const searchParams = useSearchParams();
  let t5;
  if ($[4] !== searchParams) {
    t5 = searchParams.get(SEARCH_PARAM_KEYS.KEYWORD) || "";
    $[4] = searchParams;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const keyword = t5;
  let t6;
  if ($[6] !== searchParams) {
    t6 = searchParams.get(SEARCH_PARAM_KEYS.TAB);
    $[6] = searchParams;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const tab = t6;
  const {
    store
  } = useConfigsContext();
  const htcToken = store?.credentials?.htcToken;
  let t7;
  if ($[8] !== followStatus) {
    t7 = followStatus.reduce(_temp, new Map());
    $[8] = followStatus;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const initialFollowStatusMap = t7;
  const [followStatusMap, t8] = useMap(initialFollowStatusMap);
  const {
    set
  } = t8;
  let t9;
  if ($[10] !== set) {
    t9 = statuses => {
      statuses.forEach(status_0 => {
        set(status_0.preferredUsername, status_0);
      });
    };
    $[10] = set;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const updateFollowStatusMap = t9;
  let t10;
  if ($[12] !== keyword || $[13] !== tab) {
    t10 = (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, keyword, tab);
    $[12] = keyword;
    $[13] = tab;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== htcToken || $[16] !== updateFollowStatusMap) {
    t11 = createFetcher(htcToken, updateFollowStatusMap);
    $[15] = htcToken;
    $[16] = updateFollowStatusMap;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== initialAccounts || $[19] !== initialTotal) {
    t12 = createFallbackData(initialAccounts, initialTotal);
    $[18] = initialAccounts;
    $[19] = initialTotal;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = {
      revalidateFirstPage: false,
      fallbackData: t12
    };
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const {
    data,
    isLoading,
    isValidating,
    size,
    setSize
  } = useSWRInfinite(t10, t11, t13);
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      threshold: 0.1
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const {
    isIntersecting,
    ref: loadMoreRef
  } = useIntersectionObserver(t14);
  let t15;
  bb0: {
    if (enablePagination) {
      let t16;
      if ($[24] !== data || $[25] !== followStatusMap) {
        t16 = data?.flatMap(_temp2).filter(_temp3).map(account_0 => convertAccountToCreatorCardFormat(account_0, followStatusMap)) || [];
        $[24] = data;
        $[25] = followStatusMap;
        $[26] = t16;
      } else {
        t16 = $[26];
      }
      t15 = t16;
      break bb0;
    }
    let t16;
    if ($[27] !== initialAccounts || $[28] !== initialFollowStatusMap) {
      let t17;
      if ($[30] !== initialFollowStatusMap) {
        t17 = account_1 => convertAccountToCreatorCardFormat(account_1, initialFollowStatusMap);
        $[30] = initialFollowStatusMap;
        $[31] = t17;
      } else {
        t17 = $[31];
      }
      t16 = initialAccounts.map(t17);
      $[27] = initialAccounts;
      $[28] = initialFollowStatusMap;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    t15 = t16;
  }
  const accounts = t15;
  const total = data?.[0]?.pages?.total || 0;
  const enableLoadMore = enablePagination && total > accounts.length;
  let t16;
  let t17;
  if ($[32] !== enableLoadMore || $[33] !== isIntersecting || $[34] !== isLoading || $[35] !== isValidating || $[36] !== setSize || $[37] !== size) {
    t16 = () => {
      if (isIntersecting && enableLoadMore && !isLoading && !isValidating) {
        setSize(size + 1);
      }
    };
    t17 = [isIntersecting, enableLoadMore, isLoading, isValidating, setSize, size];
    $[32] = enableLoadMore;
    $[33] = isIntersecting;
    $[34] = isLoading;
    $[35] = isValidating;
    $[36] = setSize;
    $[37] = size;
    $[38] = t16;
    $[39] = t17;
  } else {
    t16 = $[38];
    t17 = $[39];
  }
  useEffect(t16, t17);
  if (accounts.length === 0) {
    return null;
  }
  let t18;
  if ($[40] !== t) {
    t18 = t("search-page.account-results-title");
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t18) {
    t19 = <SectionHeader title={t18} viewAllHref="/creators" />;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== accounts) {
    t20 = accounts.map(_temp4);
    $[44] = accounts;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <div className="flex flex-col gap-4 lg:gap-5">{t20}</div>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== enableLoadMore || $[49] !== loadMoreRef) {
    t22 = enableLoadMore && <div ref={loadMoreRef} className="h-4" />;
    $[48] = enableLoadMore;
    $[49] = loadMoreRef;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== enablePagination || $[52] !== isLoading || $[53] !== isValidating) {
    t23 = (isLoading || isValidating) && enablePagination && <LoadingDots />;
    $[51] = enablePagination;
    $[52] = isLoading;
    $[53] = isValidating;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t19 || $[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
    t24 = <div className="flex flex-col gap-4 lg:gap-5">{t19}{t21}{t22}{t23}</div>;
    $[55] = t19;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  return t24;
}
function _temp4(creator, index) {
  return <CreatorCard key={`${creator.profileId}-${index}`} creator={creator} />;
}
function _temp3(account) {
  return account !== undefined;
}
function _temp2(page) {
  return page?.results || [];
}
function _temp(acc, status) {
  acc.set(status.preferredUsername, status);
  return acc;
}